import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCashOuts } from '../services/cashOutService';
import ScrollHint from '../components/ScrollHint';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useLocationContext } from '../contexts/LocationContext';

const CashOutList = () => {
  const { currentLocation } = useLocationContext();
  const [cashOuts, setCashOuts] = useState([]);
  // Only widen the table for washes that actually have a dog wash.
  const hasDogWash = cashOuts.some((co) => (co.dog_total || 0) > 0);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0
  });

  useEffect(() => {
    fetchCashOuts();
  }, [pagination.limit, pagination.offset, currentLocation?.id]);

  const fetchCashOuts = async () => {
    try {
      setLoading(true);
      const data = await getCashOuts({
        limit: pagination.limit,
        offset: pagination.offset
      });
      setCashOuts(data.cash_outs);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (error) {
      console.error('Error fetching cash outs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({
        ...prev,
        offset: Math.max(0, prev.offset - prev.limit)
      }));
    }
  };

  const handleNextPage = () => {
    if (pagination.offset + pagination.limit < pagination.total) {
      setPagination(prev => ({
        ...prev,
        offset: prev.offset + prev.limit
      }));
    }
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Build export rows from cash out data
  const buildExportData = (data) => {
    return data.map((co, index) => {
      // Calculate daily average
      let dailyAvg = 'N/A';
      if (index < data.length - 1) {
        const currentDate = new Date(co.date + 'T12:00:00');
        const prevDate = new Date(data[index + 1].date + 'T12:00:00');
        const days = Math.round((currentDate - prevDate) / (1000 * 60 * 60 * 24));
        if (days > 0) dailyAvg = (co.total_revenue / days).toFixed(2);
      }

      const row = {
        'Date': co.date,
        'Total Revenue': co.total_revenue,
        'SS Total': co.ss_total,
        'Auto Wash': co.auto_total,
        'Vacuums': co.vac_total,
        'Vending': co.vending_total,
        'Dog Wash': co.dog_total || 0,
        'Cash to Deposit': co.cash_to_deposit,
        'Changer Overage/Shortfall': -co.changer_net_shortfall,
        'Quarters Gained/Lost': co.quarters_gained_lost,
        'SS Rev % Coins': (co.ss_rev_percent_coins * 100).toFixed(2) + '%',
        'Daily Avg Revenue': dailyAvg
      };

      // Add per-bay details
      if (co.ss_bays) {
        co.ss_bays.forEach(bay => {
          row[`Bay ${bay.bay_number} Bills`] = bay.bills;
          row[`Bay ${bay.bay_number} Coins (qtrs)`] = bay.coins_quarters;
          row[`Bay ${bay.bay_number} Rev`] = bay.total_revenue;
        });
      }

      // Add per-vacuum details
      if (co.vacuums) {
        co.vacuums.forEach(vac => {
          row[`Vac ${vac.vacuum_number} Coins (qtrs)`] = vac.coins_quarters;
          row[`Vac ${vac.vacuum_number} Rev`] = vac.total_revenue;
        });
      }

      return row;
    });
  };

  const handleExport = async (format) => {
    try {
      // Fetch all cash outs
      const allData = await getCashOuts({ limit: 9999, offset: 0 });
      const exportRows = buildExportData(allData.cash_outs);

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Cash Outs');

      if (format === 'xlsx') {
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `cash_outs_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else {
        const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `cash_outs_export_${new Date().toISOString().split('T')[0]}.csv`);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Cash Outs</h1>
          <div className="flex items-center space-x-3">
            <Link to="/pl" className="text-sm text-blue-600 hover:text-blue-800">&larr; Back to P&L</Link>
            <div className="hidden sm:flex space-x-3">
              <button
                onClick={() => handleExport('csv')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm"
              >
                Export CSV
              </button>
              <button
                onClick={() => handleExport('xlsx')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm"
              >
                Export Excel
              </button>
              <Link
                to="/cash-out/new"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium text-sm"
              >
                New Cash Out
              </Link>
            </div>
          </div>
        </div>
        <div className="sm:hidden grid grid-cols-3 gap-2 mt-3">
          <button
            onClick={() => handleExport('csv')}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-xs text-center"
          >
            Export CSV
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-xs text-center"
          >
            Export Excel
          </button>
          <Link
            to="/cash-out/new"
            className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded-lg font-medium text-xs text-center"
          >
            New Cash Out
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <ScrollHint>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Self-Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auto Wash
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vacuums
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vending
                </th>
                {hasDogWash && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dog Wash
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Changer Overage/Shortfall
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cashOuts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No cash outs yet. <Link to="/cash-out/new" className="text-blue-600 hover:text-blue-800">Create your first one</Link>
                  </td>
                </tr>
              ) : (
                cashOuts.map((cashOut) => (
                  <tr key={cashOut.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Date(cashOut.date + 'T12:00:00').toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      ${cashOut.total_revenue.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${cashOut.ss_total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${cashOut.auto_total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${cashOut.vac_total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${cashOut.vending_total.toFixed(2)}
                    </td>
                    {hasDogWash && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${(cashOut.dog_total || 0).toFixed(2)}
                      </td>
                    )}
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${
                      cashOut.changer_net_shortfall > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      ${(-cashOut.changer_net_shortfall).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/cash-out/${cashOut.id}`}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                      >
                        View
                      </Link>
                      <Link
                        to={`/cash-out/edit/${cashOut.id}`}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ScrollHint>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={pagination.offset === 0}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={pagination.offset + pagination.limit >= pagination.total}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashOutList;
